import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/auth-context";
import { removeLoginCookie } from "components/LoginForm/LoginFormDao";
import { toastrError, toastrSuccess } from "lib/toastr";
import { deactivateAccount, deleteAccount } from "./Account.api";
import { FiAlertTriangle, FiPauseCircle, FiTrash2 } from "react-icons/fi";

type AccountAction = "deactivate" | "delete";

const AccountActionsModal = ({ onClose }: { onClose: () => void }) => {
  const [pendingAction, setPendingAction] = useState<AccountAction | null>(null);
  const { setAuth } = useAuth();
  const navigate = useNavigate();

  const finishAccountAction = () => {
    removeLoginCookie("setLoginCookie");
    localStorage.removeItem("bonusPopup");
    localStorage.removeItem("access_token");
    localStorage.removeItem("joinQualPopup");
    localStorage.removeItem("auth_ls");
    setAuth(null);
    navigate("/", { replace: true });
  };

  const handleAction = async (action: AccountAction) => {
    try {
      setPendingAction(action);
      const response = action === "delete" ? await deleteAccount() : await deactivateAccount();
      if (!response?.success) {
        throw new Error(response?.message || `Unable to ${action} account`);
      }
      toastrSuccess(response.message);
      finishAccountAction();
    } catch (error: any) {
      toastrError(error?.response?.data?.message || error?.message || "Account action failed");
      setPendingAction(null);
    }
  };

  const isPending = pendingAction !== null;

  return (
    <div className="zs-backdrop" role="dialog" aria-modal="true" aria-labelledby="account-actions-title">
      <div className="zs-modal max-w-md">
        <div className="flex items-start gap-3 px-5 pt-5 pb-4 border-b sm:px-6 border-line">
          <span className="inline-flex items-center justify-center rounded w-9 h-9 shrink-0 bg-danger-soft text-danger">
            <FiAlertTriangle />
          </span>
          <div className="min-w-0">
            <h2 id="account-actions-title" className="text-base font-semibold font-display text-ink">
              Manage your account
            </h2>
            <p className="mt-1 text-sm text-ink-3">
              Deactivation prevents future sign-ins. Deleting permanently removes your user account and cannot be undone.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-3 p-4 border rounded border-line bg-surface-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm font-medium text-ink">
                <FiPauseCircle className="text-warning" /> Deactivate
              </p>
              <p className="mt-0.5 text-xs text-ink-3">Prevents future sign-ins to this account.</p>
            </div>
            <button
              type="button"
              disabled={isPending}
              onClick={() => handleAction("deactivate")}
              className="zs-btn zs-btn-secondary zs-btn-sm text-warning border-warning/40 shrink-0"
            >
              {pendingAction === "deactivate" ? "Deactivating..." : "Deactivate account"}
            </button>
          </div>
          <div className="flex flex-col gap-3 p-4 border rounded border-danger/30 bg-danger-soft sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm font-medium text-danger">
                <FiTrash2 /> Delete permanently
              </p>
              <p className="mt-0.5 text-xs text-ink-3">This cannot be undone.</p>
            </div>
            <button
              type="button"
              disabled={isPending}
              onClick={() => handleAction("delete")}
              className="zs-btn zs-btn-danger zs-btn-sm shrink-0"
            >
              {pendingAction === "delete" ? "Deleting..." : "Delete account"}
            </button>
          </div>
        </div>
        <div className="flex justify-end px-5 py-4 border-t sm:px-6 border-line bg-surface-2 rounded-b-xl">
          <button type="button" disabled={isPending} onClick={onClose} className="w-full zs-btn zs-btn-secondary sm:w-auto">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountActionsModal;
